import React, { useState } from 'react';
import { useAuthContext } from '../../../context/AuthContext';
import { ChatMessagePayload } from '../../../hooks/useChatSocket';

interface PaymentPendingCardProps {
    message: ChatMessagePayload;
    onCompletePayment: (bookingUid: string, bookingId: number) => Promise<void>;
    onRequestOther: () => void;
}

export const PaymentPendingCard: React.FC<PaymentPendingCardProps> = ({
    message,
    onCompletePayment,
    onRequestOther
}) => {
    const { user } = useAuthContext();
    const isConsultant = user?.type_account !== 'professional';
    
    // We assume the date accepted is in metadata.acceptedDate
    const dateStr = message.metadata?.acceptedDate || '';
    
    const formatDate = (val: string) => {
        if (!val) return { dateString: "Fecha pendiente", timeString: "" };
        try {
            const normalizedVal = val.includes('T') ? val : val.replace(' ', 'T') + 'Z';
            const date = new Date(normalizedVal);
            const formatted = new Intl.DateTimeFormat("es-UY", {
                weekday: "long",
                day: "numeric",
                month: "long",
                timeZone: 'UTC'
            }).format(date);
            const time = new Intl.DateTimeFormat("es-UY", {
                hour: '2-digit',
                minute: '2-digit',
                timeZone: 'UTC'
            }).format(date);
            return { dateString: formatted.charAt(0).toUpperCase() + formatted.slice(1), timeString: time };
        } catch {
            return { dateString: val, timeString: '' };
        }
    };

    const { dateString, timeString } = formatDate(dateStr);
    const [loading, setLoading] = useState(false);
    const [timeLeft, setTimeLeft] = useState('30 min 00 s');

    React.useEffect(() => {
        const calculateTimeLeft = () => {
            const now = new Date().getTime();
            const created = new Date(message.timestamp).getTime();
            const expires = created + 30 * 60 * 1000;
            const diff = expires - now;

            if (diff <= 0) {
                setTimeLeft('00 min 00 s');
                return false; // stop polling
            }

            const minutes = Math.floor(diff / (1000 * 60));
            const seconds = Math.floor((diff % (1000 * 60)) / 1000);
            setTimeLeft(`${String(minutes).padStart(2, '0')} min ${String(seconds).padStart(2, '0')} s`);
            return true;
        };

        if (calculateTimeLeft()) {
            const interval = setInterval(calculateTimeLeft, 1000);
            return () => clearInterval(interval);
        }
    }, [message.timestamp]);

    const handlePayment = async () => {
        setLoading(true);
        await onCompletePayment(message.metadata?.bookingUid || '', message.metadata?.bookingId || 0);
        setLoading(false);
    };

    return (
        <article className="system-message">
            <div className="system-title">
                <span className="status-icon">$</span>
                <span className="system-title-copy">
                    <strong>Horario elegido · pago pendiente</strong>
                    <span>{dateString} · {timeString} · {message.metadata?.modality || 'Presencial'}</span>
                </span>
            </div>
            <div className="deadline">
                <span>Horario retenido durante el pago</span>
                <strong>{timeLeft}</strong>
            </div>
            {isConsultant ? (
                <div className="chat-actions">
                    <button 
                        className="chat-button primary" 
                        type="button" 
                        onClick={handlePayment}
                        disabled={loading}
                    >
                        {loading ? 'Procesando...' : 'Completar pago'}
                    </button>
                    <button 
                        className="chat-button secondary" 
                        type="button" 
                        onClick={onRequestOther}
                        disabled={loading}
                    >
                        Otro horario
                    </button>
                </div>
            ) : (
                <p className="system-foot">El paciente recibirá una notificación cuando el pago se acredite o finalice el plazo.</p>
            )}
        </article>
    );
};
